import { FilterOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Drawer, Input, Space } from 'antd';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useLoaderData } from 'react-router-dom';
import { getProjectsBasicInfo } from '../../api/project';
import { SelectedFilters } from '../../components/data/selected-filters.jsx';
import ProjectBasicInfoFilterForm from '../../components/form/project/project-basic-info-filter.jsx';
import { Page } from '../../components/page';
import { InfiniteTable } from '../../components/table';
import { useFetch } from '../../hooks';
import { Server } from '../../library/constants/server';
import { searchParamsToObject } from '../../library/helpers/index.js';
import { formatDateTime, formatPrice } from '../../library/utilities/intl.js';

const FILTER_NAMES = {
  search: 'Project ID',
  createdAt: 'Received',
  country_id: 'Country',
  language_id: 'Language',
}

// Read-only view for the `project_basic_info` permission (marketing role).
// The endpoint only returns these fields, so nothing else can be shown here.
const columns = [
  { title: 'Project ID', dataIndex: 'id', sorter: true },
  { title: 'Source', dataIndex: 'source' },
  { title: 'Received', dataIndex: 'createdAt', render: formatDateTime, sorter: true },
  { title: 'Order Value', dataIndex: 'order_value', render: value => formatPrice(value ?? 0, 'EUR'), sorter: true },
  {
    title: 'Country',
    dataIndex: 'country',
    sorter: true,
    render: (country, { country_flag_url }) => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        {country_flag_url && <img src={`${Server.BaseUrl}/${country_flag_url}`} alt={country} title={country} width={24} />}
        {country}
      </span>
    ),
  },
  { title: 'Language', dataIndex: 'language', sorter: true },
]

function ProjectBasicInfo({ title }) {
  const initialData = useLoaderData()
  const [filtersVisible, setFiltersVisible] = useState(false)
  const [list, hasMore, isLoading, searchParamsParsed, {
    setSort,
    setFilters,
    loadMore
  }] = useFetch({ initialData })

  return (
    <Page title={title}
      backLink={false}
      showNotifications={false}
      summary={initialData?.meta?.totalCount ? `(${initialData?.meta?.totalCount} Records)` : '(No Records)'}
      header={(
        <>
          <div></div>
          <Space.Compact style={{ width: '100%', maxWidth: '400px' }}>
            <Input.Search
              placeholder="Search by Project ID"
              defaultValue={searchParamsParsed?.filter?.search}
              prefix={<SearchOutlined />}
              onChange={(e) => setFilters((filters = {}) => ({ ...filters, 'search': e.target.value }))}
              allowClear
            />
            <Button onClick={() => setFiltersVisible(v => !v)} icon={<FilterOutlined />}>
              Filters
            </Button>
          </Space.Compact>
          <div></div>
        </>
      )}
      subHeader={initialData.meta?.filters && Object.keys(initialData.meta.filters).length > 0 && (
        <SelectedFilters filters={initialData.meta.filters} filterNames={FILTER_NAMES} updateFilters={setFilters} />
      )}
    >
      <Drawer title="Filters" open={filtersVisible} onClose={() => setFiltersVisible(false)}>
        <ProjectBasicInfoFilterForm
          key={JSON.stringify(searchParamsParsed?.filter)}
          data={searchParamsParsed?.filter}
          onFinish={(filters) => {
            setFilters(({ search } = {}) => ({ ...filters, search }))
            setFiltersVisible(false)
          }}
        />
      </Drawer>
      <InfiniteTable
        columns={columns}
        data={list}
        rowKey="id"
        isLoading={isLoading}
        onChange={setSort}
        hasMore={hasMore}
        loadMore={loadMore}
      />
    </Page>
  )
}

ProjectBasicInfo.Loader = async ({ request }) => {
  const url = new URL(request.url)
  const { filter, page, sort } = searchParamsToObject(url.searchParams)
  // Same createdAt -> start_date/end_date split as the Projects list loader.
  if (filter?.createdAt) {
    const [start_date = null, end_date = null] = filter.createdAt.split(',')
    if (start_date) {
      filter.start_date = start_date
      filter.end_date = end_date
      delete filter.createdAt
    }
  }
  const { data, meta } = await getProjectsBasicInfo({ filter, page, ...sort })
  return { data, meta }
}

ProjectBasicInfo.propTypes = {
  title: PropTypes.string,
}

export default ProjectBasicInfo
